import { placementFailureOfCode } from '@blesk/contracts';
import { ApiRequestError, apiPost } from './api';
import { SECTIONS } from './nav';

/**
 * §15.1 ТЗ, спайк S3. Вход из встроенной панели Bitrix24.
 *
 * Мест размещения два, и приходят они по-разному.
 *
 * Страница приложения: шлюз VibeCode возвращает сотрудника на наш адрес с
 * одноразовым кодом в строке запроса — `/?code=…&placement=…&member_id=…`.
 * Браузер этот код только передаёт: меняет его на сессию сервер, и
 * пользовательский токен портала в страницу не попадает вовсе (§18).
 *
 * Пункт левого меню: портал открывает встройку формой `POST` прямо на наш
 * адрес, сервер меняет контекст на сессию и возвращает браузер сюда обычным
 * переходом. Кода в адресе нет — есть только контекст вызова.
 *
 * Код одноразовый и живёт минуты, поэтому из адреса он убирается сразу — иначе
 * останется в истории браузера и в заголовке `Referer` внешних запросов.
 */
const CODE_PARAM = 'code';
const PLACEMENT_PARAM = 'placement';
const OPTIONS_PARAM = 'placement_options';

export async function consumePlacementCode(): Promise<'exchanged' | 'absent' | 'failed'> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get(CODE_PARAM);
  const options = params.get(OPTIONS_PARAM);
  // Пункт левого меню приходит на сервер формой POST, и обмен там уже случился:
  // до браузера доезжает только контекст вызова. Кода в этом случае нет, а
  // разобрать адрес карточки всё равно нужно — таблица разделов живёт здесь.
  if (!code && !options) return 'absent';

  const target = targetFromOptions(options);
  stripFromUrl(params, target);
  if (!code) return 'absent';

  try {
    await apiPost('/api/auth/vibecode/placement', { placementCode: code });
    return 'exchanged';
  } catch (error) {
    // Отказ шлюза не должен блокировать вход: SPA покажет обычную форму
    // (§15.1, разрыв встраивания). Но не молча — до 31.08.2026 причина здесь
    // выбрасывалась, и медленный шлюз выглядел голой формой входа без единого
    // слова. Повод едет на форму тем же параметром, каким его называет
    // серверный маршрут встройки; адрес карточки не теряется — после входа
    // паролем человек попадёт туда, куда шёл.
    presentFailure(error, target);
    return 'failed';
  }
}

function presentFailure(error: unknown, target: string | null): void {
  const failure = placementFailureOfCode(
    error instanceof ApiRequestError ? error.payload.code : undefined,
  );
  const params = new URLSearchParams({ placement: failure });
  if (target) params.set('next', target);
  window.history.replaceState(null, '', `/login?${params.toString()}`);
}

/**
 * §11.2 ТЗ, спайк S3. Какую страницу открыть внутри портала.
 *
 * Место встраивания своих параметров не имеет, но портал сворачивает строку
 * запроса страницы приложения в контекст вызова: ссылка
 * `…/marketplace/app/79/?card=requests%2F<id>` приходит к нам как
 * `placement_options: {"card":"requests/<id>","any":"79/"}`, а шлюз VibeCode
 * передаёт это поле без изменений. Так ссылка из описания задачи Bitrix24
 * открывает панель сразу на карточке, не уводя человека из портала.
 *
 * Хвост пути (`…/app/79/requests/<id>/`) для этого не годится: портал разбирает
 * его в `any`, но страницу приложения по такому адресу не показывает вовсе —
 * измерено на `ihoreca.bitrix24.ru` 19.08.2026.
 *
 * Значение приходит снаружи, поэтому маршрут сверяется с таблицей разделов:
 * незнакомый адрес — обычная панель, а не «страница не найдена».
 */
const TARGET_OPTION = 'card';

function targetFromOptions(raw: string | null): string | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const card = (parsed as Record<string, unknown>)[TARGET_OPTION];
  if (typeof card !== 'string') return null;

  const path = `/${card.split('/').filter(Boolean).join('/')}`;
  return isKnownRoute(path) ? path : null;
}

const SECTION_PATHS: ReadonlySet<string> = new Set(SECTIONS.map((section) => section.path));

/** Раздел §13 или карточка внутри него: `/incidents/<uuid>`, `/requests/<uuid>`. */
function isKnownRoute(path: string): boolean {
  if (SECTION_PATHS.has(path)) return true;
  const cut = path.lastIndexOf('/');
  const section = path.slice(0, cut);
  const id = path.slice(cut + 1);
  return SECTION_PATHS.has(section) && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id);
}

function stripFromUrl(params: URLSearchParams, target: string | null): void {
  params.delete(CODE_PARAM);
  params.delete(PLACEMENT_PARAM);
  params.delete('member_id');
  params.delete(OPTIONS_PARAM);
  const query = params.toString();
  const path = target ?? window.location.pathname;
  window.history.replaceState(null, '', `${path}${query ? `?${query}` : ''}`);
}
